import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '@/lib/apiClient';

/**
 * Outdoor high-contrast mode for the phone screens (#518 phase 2, design
 * "Outdoor high contrast"): pure white, black text, 2px dark borders and
 * solid status colors, for reading a phone in full sun on the walk.
 *
 * Per person: kept in localStorage under the signed-in user's id, so a shared
 * phone, or an admin viewing as someone else, doesn't carry one person's
 * choice to the next (Codex on #570). Off until the user is known. Storage can
 * be unavailable (private mode, blocked or full) — then the page keeps its own
 * copy for that user. Every toggle on the page stays in step through a window
 * event.
 */
const EVENT = 'punch-outdoor-change';
const keyFor = (userId: string) => `punch-outdoor:${userId.toLowerCase()}`;

// The page's own copy, per user, when the last write didn't reach storage: it
// wins over what storage still says, or every toggle's re-read after a change
// would switch it straight back.
const overrides = new Map<string, boolean>();

function read(userId: string | null): boolean {
  if (!userId) return false;
  const o = overrides.get(userId.toLowerCase());
  if (o !== undefined) return o;
  try {
    return localStorage.getItem(keyFor(userId)) === '1';
  } catch {
    return false;
  }
}

/** For tests: forget the page's own copies. */
export function resetOutdoorModeForTests() {
  overrides.clear();
}

/** The mode for one user, and its toggle (a no-op until the user is known). */
export function useOutdoorModeFor(userId: string | null): [boolean, () => void] {
  const [on, setOn] = useState(() => read(userId));
  useEffect(() => {
    setOn(read(userId));
    const sync = () => setOn(read(userId));
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [userId]);
  const toggle = useCallback(() => {
    if (!userId) return;
    const next = !read(userId);
    try {
      localStorage.setItem(keyFor(userId), next ? '1' : '0');
      overrides.delete(userId.toLowerCase());
    } catch {
      overrides.set(userId.toLowerCase(), next); // not remembered — still switch for this page
    }
    setOn(next);
    window.dispatchEvent(new Event(EVENT));
  }, [userId]);
  return [on, toggle];
}

/** The signed-in user's outdoor mode. */
export function useOutdoorMode(): [boolean, () => void] {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  return useOutdoorModeFor(me?.id ?? null);
}
